import { zodResolver } from '@hookform/resolvers/zod'
import { useParams } from '@tanstack/react-router'
import { useId, useRef, useState, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { CircleCheck } from 'lucide-react'
import { useTranslations } from 'use-intl'

import { EmailFallbackNotice } from '@/components/email-fallback-notice'
import { HoneypotField } from '@/components/honeypot-field'
import { Button, ButtonLoading } from '@/components/ui/button'
import {
  Autocomplete,
  AutocompleteContent,
  AutocompleteEmpty,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
  AutocompleteStatus,
} from '@/components/ui/autocomplete'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  GlobalFormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { parseEmailError } from '@/lib/email/validation'
import { trackTrialFormStart, trackTrialSubmit } from '@/lib/analytics/events'
import {
  trialFormSchema,
  type TrialFormValues,
} from '../data/trial-form-schema'
import {
  searchCompanies,
  type CompanySearchResult,
} from '@/server/company-search'
import { submitTrialRequest } from '@/server/trial-request'

type SearchState = 'idle' | 'loading' | 'error'

export function TrialFormDialog({
  trigger,
  source,
}: {
  trigger: React.ReactNode
  source?: string
}) {
  const t = useTranslations()
  const { locale } = useParams({ from: '/$locale' })
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<CompanySearchResult[]>([])
  const [searchState, setSearchState] = useState<SearchState>('idle')
  const [succeeded, setSucceeded] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isPending, startTransition] = useTransition()
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )
  // Funnel step 2: fire trial_form_start once, on first field input.
  const formStartedRef = useRef(false)
  const companyInputId = useId()

  const form = useForm<TrialFormValues>({
    resolver: zodResolver(trialFormSchema as never),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      selectedCompany: null,
      phone: '',
      website: '',
    },
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  })

  const selectedCompany = form.watch('selectedCompany')

  const translateEmailError = (key?: string) => {
    if (!key) return undefined
    const parsed = parseEmailError(key)
    return t(`cta.trialForm.errors.${parsed.key}`, parsed.params ?? {})
  }

  const translateFieldError = (key?: string) =>
    key ? t(`cta.trialForm.errors.${key}`) : undefined

  const handleCompanyInput = (value: string) => {
    setQuery(value)
    if (form.getValues('selectedCompany')) {
      form.setValue('selectedCompany', null)
    }
    clearTimeout(debounceRef.current)

    if (value.trim().length < 2) {
      setResults([])
      setSearchState('idle')
      return
    }

    debounceRef.current = setTimeout(async () => {
      setSearchState('loading')
      setResults([])
      const response = await searchCompanies({ data: { query: value.trim() } })
      if (!response.ok) {
        setSearchState('error')
        setResults([])
        return
      }
      setResults(response.results)
      setSearchState('idle')
    }, 300)
  }

  const handleCompanySelect = (siren: string | null) => {
    if (!siren) {
      form.setValue('selectedCompany', null)
      return
    }
    const company = results.find((item) => item.siren === siren)
    if (!company) return
    // Selecting fires an input-value change with the label; cancel that search.
    clearTimeout(debounceRef.current)
    setResults([])
    form.setValue('selectedCompany', company, { shouldValidate: true })
  }

  const onSubmit = (values: TrialFormValues) => {
    if (!values.selectedCompany) return
    setSubmitError(null)
    startTransition(async () => {
      const response = await submitTrialRequest({
        data: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          company: values.selectedCompany!,
          phone: values.phone,
          locale,
          website: values.website,
        },
      })
      if (!response.ok) {
        setSubmitError(t('cta.trialForm.submitError'))
        return
      }
      setSucceeded(true)
      trackTrialSubmit({ source })
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) {
          clearTimeout(debounceRef.current)
          formStartedRef.current = false
          form.reset()
          setQuery('')
          setResults([])
          setSearchState('idle')
          setSucceeded(false)
          setSubmitError(null)
        }
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="gap-0 overflow-hidden p-0 sm:max-w-3xl md:min-h-[27rem]"
        onInteractOutside={(event) => {
          // The company autocomplete popup is portaled to document.body,
          // i.e. outside the dialog — don't let interacting with it dismiss.
          const target = event.target as Element | null
          if (target?.closest('[data-slot=autocomplete-popup]')) {
            event.preventDefault()
          }
        }}
      >
        <div className="grid md:grid-cols-2">
          <aside className="hidden flex-col gap-8 overflow-hidden bg-primary p-8 pb-0 text-primary-foreground md:flex">
            <div
              aria-hidden
              className="h-7 w-44 shrink-0 bg-primary-foreground"
              style={{
                WebkitMaskImage: 'url(/full-logo.webp)',
                maskImage: 'url(/full-logo.webp)',
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskSize: 'contain',
                maskSize: 'contain',
                WebkitMaskPosition: 'left center',
                maskPosition: 'left center',
              }}
            />
            <img
              src="/mascot/mascot-cross-arm.webp"
              alt=""
              width={642}
              height={468}
              className="mt-auto w-full self-center object-contain"
            />
          </aside>
          <div className="flex flex-col gap-4 p-6">
            {succeeded ? (
              <div className="flex flex-1 flex-col justify-center gap-6">
                <CircleCheck className="size-12 text-success" aria-hidden />
                <DialogHeader>
                  <DialogTitle>{t('cta.trialForm.successHeading')}</DialogTitle>
                  <DialogDescription>
                    {t('cta.trialForm.success')}
                  </DialogDescription>
                </DialogHeader>
                <EmailFallbackNotice />
                <DialogClose asChild>
                  <Button variant="default" className="w-full">
                    {t('cta.trialForm.close')}
                  </Button>
                </DialogClose>
              </div>
            ) : (
              <Form {...form}>
                <DialogHeader>
                  <DialogTitle>{t('cta.trialForm.title')}</DialogTitle>
                  <DialogDescription>
                    {t('cta.trialForm.description')}
                  </DialogDescription>
                </DialogHeader>
                <form
                  onSubmit={form.handleSubmit(onSubmit)}
                  // Native input events bubble from every field to the form, so
                  // one listener catches the user's first keystroke anywhere.
                  onInput={() => {
                    if (!formStartedRef.current) {
                      formStartedRef.current = true
                      trackTrialFormStart({ source })
                    }
                  }}
                  className="my-2 grid gap-4"
                  noValidate
                >
                  <FormField
                    control={form.control}
                    name="selectedCompany"
                    render={({ fieldState }) => (
                      <FormItem>
                        <FormLabel htmlFor={companyInputId} required>
                          {t('cta.trialForm.companyNameLabel')}
                        </FormLabel>
                        <Autocomplete
                          onInputValueChange={handleCompanyInput}
                          onValueChange={handleCompanySelect}
                        >
                          <AutocompleteInput
                            id={companyInputId}
                            size="lg"
                            showClear
                            showTrigger
                            placeholder={t(
                              'cta.trialForm.companyNamePlaceholder',
                            )}
                          />
                          {(searchState !== 'idle' ||
                            results.length > 0 ||
                            query.trim().length >= 2) && (
                            <AutocompleteContent className="pointer-events-auto">
                              <AutocompleteList
                                onWheel={(event) => {
                                  // Radix's modal scroll-lock preventDefaults the
                                  // wheel event, so scroll the list ourselves.
                                  const el = event.currentTarget
                                  el.scrollTop +=
                                    event.deltaMode === 1
                                      ? event.deltaY * 16
                                      : event.deltaY
                                }}
                              >
                                {searchState === 'loading' && (
                                  <AutocompleteStatus>
                                    {t('cta.trialForm.searching')}
                                  </AutocompleteStatus>
                                )}
                                {searchState === 'error' && (
                                  <AutocompleteEmpty>
                                    {t('cta.trialForm.searchError')}
                                  </AutocompleteEmpty>
                                )}
                                {searchState === 'idle' &&
                                  results.length === 0 &&
                                  query.trim().length >= 2 && (
                                    <AutocompleteEmpty>
                                      {t('cta.trialForm.noResults')}
                                    </AutocompleteEmpty>
                                  )}
                                {results.map((company) => (
                                  <AutocompleteItem
                                    key={company.siren}
                                    value={company.siren}
                                    label={company.name}
                                  >
                                    <div className="animate-in fade-in-0 slide-in-from-left-1 flex w-full flex-col duration-300">
                                      <span className="font-medium">
                                        {company.name}
                                      </span>
                                      <span className="text-xs text-muted-foreground">
                                        {company.address}
                                      </span>
                                    </div>
                                  </AutocompleteItem>
                                ))}
                              </AutocompleteList>
                            </AutocompleteContent>
                          )}
                        </Autocomplete>
                        {fieldState.error && (
                          <FormMessage>
                            {t('cta.trialForm.selectRequired')}
                          </FormMessage>
                        )}
                      </FormItem>
                    )}
                  />

                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel required>
                            {t('cta.trialForm.firstNameLabel')}
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              autoComplete="given-name"
                              placeholder={t(
                                'cta.trialForm.firstNamePlaceholder',
                              )}
                            />
                          </FormControl>
                          {fieldState.error?.message && (
                            <FormMessage>
                              {translateFieldError(fieldState.error.message)}
                            </FormMessage>
                          )}
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field, fieldState }) => (
                        <FormItem>
                          <FormLabel required>
                            {t('cta.trialForm.lastNameLabel')}
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              autoComplete="family-name"
                              placeholder={t(
                                'cta.trialForm.lastNamePlaceholder',
                              )}
                            />
                          </FormControl>
                          {fieldState.error?.message && (
                            <FormMessage>
                              {translateFieldError(fieldState.error.message)}
                            </FormMessage>
                          )}
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel required>
                          {t('cta.trialForm.emailLabel')}
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="email"
                            autoComplete="email"
                            placeholder={t('cta.trialForm.emailPlaceholder')}
                          />
                        </FormControl>
                        {fieldState.error?.message && (
                          <FormMessage>
                            {translateEmailError(fieldState.error.message)}
                          </FormMessage>
                        )}
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>{t('cta.trialForm.phoneLabel')}</FormLabel>
                        <FormControl>
                          <PhoneInput
                            defaultCountry="FR"
                            value={field.value ?? ''}
                            onChange={(value) => field.onChange(value ?? '')}
                            onBlur={field.onBlur}
                            name={field.name}
                          />
                        </FormControl>
                        {fieldState.error?.message && (
                          <FormMessage>
                            {translateFieldError(fieldState.error.message)}
                          </FormMessage>
                        )}
                      </FormItem>
                    )}
                  />

                  <HoneypotField register={form.register as never} />

                  {submitError && <GlobalFormMessage message={submitError} />}

                  <ButtonLoading
                    type="submit"
                    loading={isPending}
                    disabled={!selectedCompany}
                    className="w-full"
                  >
                    {t('cta.trialForm.submit')}
                  </ButtonLoading>
                </form>
              </Form>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
